/**
 * The staff-recorded ACH debit authorization every staff-started bank debit
 * needs: how the customer authorized it (in person / phone / written) and a
 * checkbox confirming they authorized the exact text, which names the payer,
 * the business, the amount and the date. The server refuses a staff-started
 * bank charge without it and records who took it.
 *
 * Shared by the "charge a new card/bank account" collector and the saved-method
 * charges in the Contact and Invoice dialogs: `useAchAuthorization` owns the
 * state (`@/hooks/payments/useAchAuthorization`), this renders it.
 */
import { Loader2, ShieldAlert } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/atoms/alert';
import { Button } from '@/components/atoms/button';
import { Checkbox } from '@/components/atoms/checkbox';
import { Label } from '@/components/atoms/label';
import { RadioGroup, RadioGroupItem } from '@/components/atoms/radio-group';
import type { AchAuthorizationState } from '@/hooks/payments/useAchAuthorization';
import type { StaffChargeAuthorizationChannel } from '@/types/contact';

const AUTH_CHANNELS: { value: StaffChargeAuthorizationChannel; label: string }[] = [
  { value: 'IN_PERSON', label: 'In person' },
  { value: 'PHONE', label: 'Phone' },
  { value: 'WRITTEN', label: 'Written' },
];

export function AchAuthorizationFields({
  authorization,
  disabled,
  showNoEmailWarning,
  noAccessMessage = "You don't have access to the company name needed to charge a bank account: the authorization has to name your business. Ask an admin.",
}: {
  authorization: AchAuthorizationState;
  disabled?: boolean;
  /** The payer has no email on file, so no copy of the authorization can go out. */
  showNoEmailWarning: boolean;
  noAccessMessage?: string;
}) {
  const { company, channel, setChannel, confirmed, setConfirmed, text } = authorization;
  return (
    <div className="space-y-3 rounded-lg border border-border p-3">
      <div className="space-y-2">
        <Label>How was this authorized?</Label>
        <RadioGroup
          value={channel ?? ''}
          onValueChange={(value) => setChannel(value as StaffChargeAuthorizationChannel)}
          disabled={disabled}
          className="grid grid-cols-3 gap-2"
        >
          {AUTH_CHANNELS.map((option) => (
            <label
              key={option.value}
              className="flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm"
            >
              <RadioGroupItem value={option.value} />
              {option.label}
            </label>
          ))}
        </RadioGroup>
      </div>
      {text ? (
        <label className="flex items-start gap-2 text-sm">
          <Checkbox
            checked={confirmed}
            onCheckedChange={(checked) => setConfirmed(Boolean(checked))}
            disabled={disabled}
          />
          <span>The customer authorized this debit: &ldquo;{text}&rdquo;</span>
        </label>
      ) : authorization.businessName && !authorization.payerNameKnown ? (
        authorization.payerNameLoading ? (
          <p role="status" className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading the account holder&apos;s name for the authorization…
          </p>
        ) : (
          <Alert variant="destructive">
            <ShieldAlert className="h-4 w-4" />
            <AlertTitle>Can&apos;t take a bank authorization</AlertTitle>
            <AlertDescription>
              The authorization has to name the account holder, and their name isn&apos;t available.
              Add a name to their contact first.
            </AlertDescription>
          </Alert>
        )
      ) : authorization.businessName && !authorization.amountEntered ? (
        <p className="text-sm text-muted-foreground">
          Enter an amount to see the authorization the customer is giving.
        </p>
      ) : authorization.businessName && !authorization.amountKnown ? (
        <p role="status" className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Working out the debit amount for the authorization…
        </p>
      ) : company.status === 'loading' ? (
        <p role="status" className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading your business name for the authorization…
        </p>
      ) : (
        <Alert variant="destructive">
          <ShieldAlert className="h-4 w-4" />
          <AlertTitle>Can&apos;t take a bank authorization</AlertTitle>
          {company.status === 'unavailable' && company.reason === 'no-access' ? (
            <AlertDescription>{noAccessMessage}</AlertDescription>
          ) : company.status === 'unavailable' && company.reason === 'failed' ? (
            <AlertDescription className="space-y-2">
              <p>Couldn&apos;t load your business name, which the authorization has to name.</p>
              <Button type="button" variant="outline" size="sm" onClick={company.retry}>
                Try again
              </Button>
            </AlertDescription>
          ) : (
            <AlertDescription>
              The authorization has to name your business, and the company name isn&apos;t
              available. Add it in Settings → Company, or ask an admin to.
            </AlertDescription>
          )}
        </Alert>
      )}
      {showNoEmailWarning ? (
        <Alert variant="warning">
          <ShieldAlert className="h-4 w-4" />
          <AlertDescription>
            This customer has no email on file, so they won&apos;t get a copy of this authorization.
          </AlertDescription>
        </Alert>
      ) : null}
    </div>
  );
}
